
import Component, { mixins } from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import NodeAssessment from '@/components/coach/assessment/NodeAssessment.vue';
import { Member } from '@/models/member';
import { ListOpenFirst } from '@/components/mixins/listOpenFirst';
import { GroupScope } from '@/components/mixins/groupscope';
import { GroupSchemeScope } from '@/components/mixins/schemescope';
import { SchemeNodeModel, NodeType } from '@/models/schemeNodeModel';
import ViewSkillModal from '@/components/coach/scheme/ViewSkillModal.vue';
import GroupedMemberView from '@/components/common/GroupedMemberView.vue';
import { union } from 'lodash';
import {
  isAssessible,
  getForcePassed,
  getNodeIdsFromTree,
  isManualButtonId,
  isManualOnlyLevel,
  findManualButtonNode,
  nodeRequirementsMet,
} from '@/services/utils';

@Component({
  components: {
    NodeAssessment,
    ViewSkillModal,
    GroupedMemberView,
  },
  name: 'SkillAssessmentTree',
})
export default class SkillAssessmentTree extends mixins(ListOpenFirst, GroupSchemeScope, GroupScope) {
  /**
   * levelNodeId
   *
   * This is passed in via the router and is a level node that was selected on the menu page.
   *
   * @public
   * @property {string}
   */
  @Prop() public levelNodeId!: string;

  @Prop() public selectedNodeId!: string;
  /**
   * directLinkNodeId
   *
   * When linking from the assess by member pages the link is a skill, this property is passed via the router when that link
   * is followed.  It can be Null in the case of this page being navigated to directly.  Will contain the Id of the skill node
   * followed.  This property is passed to allow expanding the list group if skill link is followed.
   *
   * @public
   * @property {string}
   */
  @Prop() public directLinkNodeId!: string;
  @Prop({ default: false }) public completeOnly!: boolean;
  @Prop({ default: null }) public manualOnlyLevel!: boolean;
  @Prop({ default: true }) public showAllMembers!: boolean;

  /**
   * dialogOpen
   * Controls visibility of the v-dialog component.
   *
   * @private
   * @property {boolean}
   */
  public dialogOpen = false;

  /**
   * dialogSelectedElement
   * This is updated when the dialog is opened and will contain the ID of the selected scheme node.
   *
   * @private
   * @property {string}
   */
  public dialogSelectedElement = '';

  private currentNodeLevelCache: Record<string, string[]> = {}; // a cache of the current limited nodes for a member

  public passAllLoading = false;
  private members = [] as Array<Member>;

  beforeMount() {
    this.loadPlannedSkills();
    this.members = this.groupMembers
      .filter(m => m.in)
      .map((m: Member) => {
        m.legend = this.memberLegend(m);
        return m;
      });
  }

  /**
   * memberUuid
   * Current members UUID.
   *
   * @param {string} nodeId
   * @return {void}
   */
  showDialog(nodeId: string): void {
    this.dialogOpen = true;
    this.dialogSelectedElement = nodeId;
  }

  isAssessible(node: SchemeNodeModel): boolean {
    return isAssessible(node);
  }

  get selectedNode() {
    return this.getNode(this.selectedNodeId);
  }

  isOpen(node: SchemeNodeModel, index: number): boolean {
    // If we have been accessed via a link, then we only open that node
    // If we have an active lesson plan, then we open only the planned skills
    // Otherwise, we open the first skill.
    if (this.directLinkNodeId) {
      return this.isLinkedToNode(node);
    }
    if (this.haveLessonPlan()) {
      return this.isPlannedSkill(node);
    }
    return this.firstOpen && index === 0;
  }

  get parentNode() {
    if (this.selectedNode.parentId) {
      return this.getNode(this.selectedNode.parentId);
    }
    return null;
  }

  get passAllButtonText() {
    return this.passAllLoading ? this.$t('global.saving') : this.$t('members.pass_all');
  }

  /**
   * Computed getter to retrieve the selected node from the store and return it's children for display.
   *
   * @return {Array<SchemeNodeModel>}
   */
  get assessmentTree(): Array<SchemeNodeModel> {
    return this.selectedNode.children.filter(
      (node: SchemeNodeModel) => (node.grade === undefined || isAssessible(node)) && this.aMemberHasSkillAvailable(node)
    );
  }

  /**
   * Computed getter to return group members from the store.
   *
   * @return {Array<Member>}
   */
  get filteredGroupMembers(): Array<Member> {
    return this.members.filter(
      (member: Member) =>
        member.in &&
        (!this.completeOnly || this.selectedNodeId in member.assessments.available) &&
        (this.showAllMembers || Object.keys(member.progress || {}).includes(this.levelNodeId))
    );
  }

  get allAdditionalAssessmentNodeIds(): Array<string> {
    if (this.group.assessment_level_limit) {
      return [];
    }
    // includes all the children of each node
    return union(
      ...this.schemeAdditionalAssessmentNodeIds.map((nodeId: string) => getNodeIdsFromTree(this.getNode(nodeId)))
    );
  }

  skillIsInLimitedLevels(member: Member, node: SchemeNodeModel): boolean {
    if (!this.group.assessment_level_limit) {
      return false;
    }
    const currentLevel = member.current_remote_node_id || 'FIRST_LEVEL';
    if (!(currentLevel in this.currentNodeLevelCache)) {
      if (currentLevel === 'FIRST_LEVEL' && !this.firstGroupLevel) {
        this.currentNodeLevelCache[currentLevel] = [];
        return false;
      }
      const levelNode = this.getNode(currentLevel === 'FIRST_LEVEL' ? this.firstGroupLevel || '' : currentLevel);
      this.currentNodeLevelCache[currentLevel] = getNodeIdsFromTree(levelNode);
    }
    return this.currentNodeLevelCache[currentLevel].includes(node.id);
  }

  /**
   * While building the list of members under each skill this is responsible for checking that the member has this particular
   * skill available.  if not we will not add them to the list of course as that would be silly eh billy.
   *
   * @param {Member} member
   * @param {SchemeNodeModel} node
   * @return {boolean}
   */
  memberHasSkillAvailable(member: Member, node: SchemeNodeModel): boolean {
    // NOTE: We no longer filter the list of members by this, we show all members in the group for ALL skills, if any
    // single member in the group has access to a skill, everyone can be assessed on it.
    // If a level is available, show all children of the level.
    const requiredSkills = node.t === NodeType.Level ? [node.id] : [node.id, this.getNearestLevel(node.id)];
    const forcePassed = getForcePassed(this.scheme, member);
    return (
      this.allAdditionalAssessmentNodeIds.includes(node.id) ||
      requiredSkills.some(nodeId => nodeId in member.assessments.available && !forcePassed.includes(nodeId)) ||
      this.skillIsInLimitedLevels(member, node)
    );
  }

  aMemberHasSkillAvailable(node: SchemeNodeModel): boolean {
    // any sibling to that skill
    return this.filteredGroupMembers.some(m => this.memberHasSkillAvailable(m, node));
  }

  /**
   * When linking from assess by member the link is from a specific sheme node.  This is responsible for assessing when
   * building the tree if a node is linked to.  If it is it is open/expanded by default.
   *
   * @param {SchemeNodeModel} childNode
   * @return {boolean}
   */
  isLinkedToNode(childNode: SchemeNodeModel): boolean {
    return this.directLinkNodeId === childNode.id;
  }

  /**
   * Formats each members name suitable for URL display, it is then attached to that members link as a param.
   *
   * @param {Member} member
   * @return {string}
   */
  getMemberLinkName(member: Member): string {
    return member.firstname.toLowerCase() + '-' + member.lastname.toLowerCase();
  }

  // Pass all skills for a level
  async passAll(node: SchemeNodeModel) {
    const res = await this.$confirm(this.$t('members.pass_all_skill_confirm'));
    if (!res) {
      return;
    }

    this.passAllLoading = true;

    // This is to allow the modal to close so we can display the loading icon on the pass all button
    setTimeout(async () => {
      let updateGroup = false;
      if (node.grade) {
        for (let index = 0; index < this.filteredGroupMembers.length; index++) {
          const member = this.filteredGroupMembers[index] as Member;
          if (await this.passSkill(member.uuid, node)) {
            updateGroup = true;
          } else {
            this.$alert('error', this.$t('members.error_pass_all_members_for_n', { skill: node.title }) as string);
          }
        }
      }

      if (updateGroup) {
        this.loadGroup();
        this.$alert('success', this.$t('members.pass_all_members_for_n', { skill: node.title }) as string);
      }
      this.passAllLoading = false;
    }, 1000);
  }

  allPassed(node: SchemeNodeModel) {
    return this.filteredGroupMembers.every(member => node.id in member.assessments.complete);
  }

  /**
   * Checks if the given node has children that are assessable.
   *
   * @param {SchemeNodeModel} node
   * @return {boolean}
   */
  nodeHasAssessableChildren(node: SchemeNodeModel): boolean {
    if (!node.children) {
      return false;
    }
    return node.children.some(isAssessible);
  }

  isManualButtonId(nodeId: string) {
    return isManualButtonId(nodeId);
  }

  isManualOnlyLevel(node: SchemeNodeModel) {
    return isManualOnlyLevel(node);
  }

  getParentNodeTitle(node: SchemeNodeModel) {
    if (this.parentNode) {
      return this.parentNode.title;
    }
    return node.id; // Shouldn't get here
  }

  manualLevelIsPassed(member: Member, node: SchemeNodeModel | null) {
    if (!node) {
      return false;
    }
    const button = findManualButtonNode(this.getNode(node.id));
    if (!button) {
      return false;
    }

    return button.id in member.assessments.complete && member.assessments.complete[button.id] === true;
  }

  isPassable(member: Member, node: SchemeNodeModel | null) {
    if (!node) {
      return false;
    }
    const button = findManualButtonNode(this.getNode(node.id));
    if (!button) {
      return false;
    }
    return nodeRequirementsMet(button.dependencies, member.assessments.complete);
  }
}
